import { clsx, type ClassValue } from "clsx";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { twMerge } from "tailwind-merge";

dayjs.extend(utc);

const SIGN_UP_ENABLED_ENVS = ["dev", "qa"] as const;
const COLOR_ENABLED_ENVS = ["dev"] as const;

type SizeItem = {
  name: string;
  w: number;
  h: number;
};

type MediaItem = {
  name: string;
  type: string;
  jmax?: string;
  size: SizeItem[];
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function isSignUpEnabledEnv(appEnv?: string) {
  return SIGN_UP_ENABLED_ENVS.includes(appEnv as (typeof SIGN_UP_ENABLED_ENVS)[number]);
}

export const isColorEnabledEnv = COLOR_ENABLED_ENVS.includes(
  process.env.NEXT_PUBLIC_APP_ENV as (typeof COLOR_ENABLED_ENVS)[number]
);

export function filterByKeyword(list: MediaItem[], keyword: string): MediaItem[] {
  const lowerKeyword = keyword.toLowerCase();
  const lowerKeywordList = lowerKeyword.split(" "); // 띄어쓰기로 구분해서 다중검색

  return list
    .map(item => {
      const hasAnyKeywordMatch = lowerKeywordList.some(keyword => {
        const nameMatch = item.name.toLowerCase().includes(keyword);
        const sizeMatch = item.size.some(size => size.name.toLowerCase().includes(keyword));
        return nameMatch || sizeMatch;
      });

      if (!hasAnyKeywordMatch) {
        return null;
      }

      const nameMatchesAny = lowerKeywordList.some(keyword =>
        item.name.toLowerCase().includes(keyword)
      );

      const matchedSizes = nameMatchesAny
        ? item.size
        : item.size.filter(size =>
            lowerKeywordList.some(keyword => size.name.toLowerCase().includes(keyword))
          );

      return {
        ...item,
        size: matchedSizes,
      };
    })
    .filter((item): item is MediaItem => item !== null);
}

export function formatTimestampDate(timestamp: number, type: "day" | "time"): string {
  const date = dayjs.unix(timestamp).local();

  if (type === "day") {
    return date.format("YYYY-MM-DD");
  }
  return date.format("HH:mm");
}

export const formatImageUrl = (url: string) => {
  if (url?.startsWith("http") || url?.startsWith("https")) {
    return url;
  } else if (url?.startsWith("data")) {
    // base64 형태로 넘어오는 경우 그대로 렌더링
    return url;
  } else {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return `${process.env.NEXT_APP_API_URL || `${origin}/api`}/fs/${encodeURIComponent(url)}`;
  }
};

export function isValidLastPathUrl(pathname: string): boolean {
  return (
    pathname === "/" ||
    pathname.startsWith("/create") ||
    pathname.startsWith("/billing") ||
    pathname.startsWith("/users") ||
    pathname.startsWith("/all") ||
    pathname.startsWith("/sales") ||
    pathname.startsWith("/group") ||
    pathname.startsWith("/dashboard") ||
    pathname.startsWith("/my-page") ||
    pathname.startsWith("/upload")
  );
}
